"use client";
import React, { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef, useMemo } from "react";
import { usePlacesMap } from "@/hooks/use-places-map";
import { useTranslation } from "@/lib/i18n";
import { API_BASE } from "@/lib/api";
import { traceApiError, traceApiResponse } from "@/lib/api-tracing";

// Define proper types for place data
interface Place {
  id: string;
  type: string;
  name?: string;
  [key: string]: unknown;
}

// Define Mapbox event types
interface MapboxFeature {
  properties: {
    id: string;
    type: string;
    [key: string]: unknown;
  };
  geometry: {
    coordinates: [number, number];
  };
}

interface MapboxEvent {
  features: MapboxFeature[];
}

// Icon management constants
const ICON_TYPES = ['foodCategory', 'shoppingCategory', 'attractionCategory', 'servicesCategory', 'emergencyCategory', 'propertyCategory'] as const;
const ICON_VARIANTS = ICON_TYPES.flatMap(type => type === 'propertyCategory' ? [type] : [type, `${type}_selected`]);

interface PropertyMapSectionProps {
  propertyId: string;
  centerLatitude: number;
  centerLongitude: number;
  zoomLevel: number;
  mapHeight: string;
  placeType?: string;
  onPlaceSelect?: (place: Place) => void;
  onPlaceHover?: (placeId: string | null) => void;
  places?: Place[];
  selectedPlaceId?: string | null;
  hoveredPlaceId?: string | null;
}

export interface PropertyMapSectionHandle {
  flyToPlace: (lat: number, lon: number) => void;
  flyToProperty: () => void;
  getMapBounds: () => {
    north: number;
    south: number;
    east: number;
    west: number;
  } | undefined;
}

export const PropertyMapSection = forwardRef<PropertyMapSectionHandle, PropertyMapSectionProps>(({
  propertyId,
  centerLatitude,
  centerLongitude,
  zoomLevel,
  mapHeight,
  placeType,
  onPlaceSelect,
  onPlaceHover,
  places = [],
  selectedPlaceId,
  hoveredPlaceId,
}, ref) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null);
  const [mapBounds, setMapBounds] = useState<{
    north: number;
    south: number;
    east: number;
    west: number;
  } | undefined>(undefined);
  const lastBoundsRef = useRef<{
    north: number;
    south: number;
    east: number;
    west: number;
  } | null>(null);
  const [iconsLoaded, setIconsLoaded] = useState(false);
  
  // Use the places map hook
  const { features } = usePlacesMap({
    propertyId,
    placeType: placeType === "all" ? undefined : placeType,
    bounds: mapBounds,
    propertyLat: centerLatitude,
    propertyLon: centerLongitude,
  });

  // Map bounds change tracking

  // Initialize the map
  useEffect(() => {
    const loadMapbox = (callback: () => void) => {
      if (typeof (window as unknown as Record<string, unknown>).mapboxgl !== "undefined") {
        callback();
        return;
      }
      const script = document.createElement("script");
      script.src = "https://api.mapbox.com/mapbox-gl-js/v2.14.1/mapbox-gl.js";
      script.onload = () => {
        if (typeof (window as unknown as Record<string, unknown>).mapboxgl === "undefined") {
          console.error("Mapbox failed to load.");
          return;
        }
        callback();
      };
      script.onerror = () => {
        console.error("Failed to load Mapbox script.");
      };
      document.body.appendChild(script);
      const link = document.createElement("link");
      link.href = "https://api.mapbox.com/mapbox-gl-js/v2.14.1/mapbox-gl.css";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    };

    const initializeMap = () => {
      const mapContainer = document.getElementById("property-map");
      if (!mapContainer) {
        console.error('Map container not found');
        return;
      }
      
      const mapboxgl = (window as unknown as Record<string, unknown>).mapboxgl;
      
      if (!mapboxgl) {
        console.error('Mapbox GL JS not loaded');
        return;
      }
      
      (mapboxgl as { accessToken: string }).accessToken =
        "pk.eyJ1IjoiZ2V0bG9zdHdvcmxkIiwiYSI6ImNtOHllcjd1MTAwZmkyanNhd3d4ZzdrajkifQ.ICF1io7h1gERlPlFSIkStQ";
      
      const map = new (mapboxgl as { Map: new (options: unknown) => unknown }).Map({
        container: "property-map",
        style: "mapbox://styles/getlostworld/cm8t7p3ci00gq01sbdifob9vb",
        center: [centerLongitude, centerLatitude],
        zoom: zoomLevel,
      });
      
      mapRef.current = map;

      (map as { on: (event: string, handler: () => void) => void }).on("load", () => {
        (map as { scrollZoom: { enable: () => void } }).scrollZoom.enable();
        
        // Consolidated icon loading function
        const loadIcons = async () => {
          const loadIcon = async (iconType: string) => {
            try {
              const response = await fetch(`/assets/img/categoryIcons/${iconType}.svg`);
              if (response.ok) {
                const svgText = await response.text();
                const img = new Image();
                
                return new Promise<void>((resolve, reject) => {
                  img.onload = () => {
                    try {
                      if ((map as { hasImage: (id: string) => boolean }).hasImage(iconType)) {
                        (map as { removeImage: (id: string) => void }).removeImage(iconType);
                      }
                      // Use consistent pixelRatio of 5 for proper icon sizing
                      (map as { addImage: (id: string, image: HTMLImageElement, options?: { pixelRatio?: number }) => void }).addImage(iconType, img, { pixelRatio: 5 });
                      resolve();
                    } catch (error) {
                      console.error(`Failed to add icon ${iconType} to map:`, error);
                      reject(error);
                    }
                  };
                  img.onerror = (err) => {
                    console.error(`Failed to load icon ${iconType} image`, err);
                    reject(new Error(`Failed to load icon ${iconType}`));
                  };
                  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
                });
              } else {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
              }
            } catch (error) {
              console.error(`Failed to load icon ${iconType}:`, error);
              // Use fallback icon (Mapbox default marker)
              try {
                if (!(map as { hasImage: (id: string) => boolean }).hasImage(iconType)) {
                  (map as { addImage: (id: string, image: HTMLImageElement) => void }).addImage(iconType, (map as { getImage: (id: string) => HTMLImageElement }).getImage('marker-15'));
                }
              } catch (fallbackError) {
                console.error(`Failed to add fallback icon for ${iconType}:`, fallbackError);
              }
            }
          };
          
          // Load all icon variants using the constant
          await Promise.allSettled(ICON_VARIANTS.map(loadIcon));
          setIconsLoaded(true);
        };
        
        loadIcons();
        
        // Add property marker (always visible)
        (map as { addSource: (id: string, source: unknown) => void }).addSource("property", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [{
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [centerLongitude, centerLatitude],
              },
              properties: {
                id: "property",
                type: "property",
              },
            }],
          },
        });

        // Add places source (empty initially)
        (map as { addSource: (id: string, source: unknown) => void }).addSource("places", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [],
          },
        });

        // Add places as circles (background)        
        (map as { addLayer: (layer: unknown) => void }).addLayer({
          id: "places-circles",
          type: "symbol",
          source: "places",
          layout: {
            "icon-image": ["get", "icon"],
            "icon-size": 1,
            "icon-allow-overlap": true,
            "icon-ignore-placement": false,
          },
          //paint: {
            //"circle-radius": 12,
            //"circle-color": ["get", "color"],
            //"circle-stroke-width": 2,
            //"circle-stroke-color": "#FFFFFF",
            // Fade out circles if fadingOut is true
            //"circle-opacity": ["case", ["boolean", ["get", "fadingOut"], false], 0.2, 1.0],
            //"circle-blur": ["case", ["boolean", ["get", "fadingOut"], false], 0.5, 0.0],
          //},
        });
        
        (map as { addLayer: (layer: unknown) => void }).addLayer({
          id: "property-marker",
          type: "symbol",
          source: "property",
          layout: {
            "icon-image": "property-icon-active",
            "icon-size": 1,
            "icon-allow-overlap": true,
            "icon-offset": [0, -10],
            "symbol-sort-key": 1000,
          },
        });

        // Add click handler for places
        (map as { on: (event: string, layer: string, handler: (e: MapboxEvent) => void) => void }).on("click", "places-circles", async (e: MapboxEvent) => {
          const startTime = performance.now();
          const feature = e.features[0];
          if (feature && onPlaceSelect && feature.properties && feature.properties.id) {
              const url = `${API_BASE}/places/${feature.properties.id}`;
              try {
              const response = await fetch(url);
              const duration = performance.now() - startTime;
              if (response.ok) {
                const data = await response.json();
                // Trace the API response
                traceApiResponse(
                  url,
                  'GET',
                  response,
                  data,
                  duration,
                  { propertyId:feature.properties.id, operation: 'fetch_place_data' }
                );

                if (data && data.id) {
                  onPlaceSelect(data);
                } else {
                  console.warn('[PropertyMapSection] API returned no data for id:', feature.properties.id);
                }
              } else {
                traceApiError(
                  url,
                  'GET',
                  new Error(`HTTP ${response.status}: ${response.statusText}`),
                  { propertyId:feature.properties.id, operation: 'fetch_place_data', duration }
                );

                console.error('[PropertyMapSection] API error for id:', feature.properties.id, response.status);
              }
            } catch (err) {
              const duration = performance.now() - startTime;
              
              traceApiError(
                url,
                'GET',
                err as Error,
                { propertyId:feature.properties.id, operation: 'fetch_place_data', duration }
              );
              
              console.error('[PropertyMapSection] Error fetching place from API:', err);
            }
          }
        });

        // Change cursor on hover
        (map as { on: (event: string, layer: string, handler: () => void) => void }).on("mouseenter", "places-circles", () => {
          (map as { getCanvas: () => HTMLCanvasElement }).getCanvas().style.cursor = "pointer";
        });

        (map as { on: (event: string, layer: string, handler: () => void) => void }).on("mouseleave", "places-circles", () => {
          (map as { getCanvas: () => HTMLCanvasElement }).getCanvas().style.cursor = "";
        });

        // Set initial bounds
        const bounds = (map as { getBounds: () => { getNorth: () => number; getSouth: () => number; getEast: () => number; getWest: () => number } }).getBounds();
        const initialBounds = {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        };
        
        // Calculate the actual radius shown by the map
        const latSpan = initialBounds.north - initialBounds.south;
        const lonSpan = initialBounds.east - initialBounds.west;
        const latRadiusKm = (latSpan / 2) * 111; // Convert degrees to km
        const lonRadiusKm = (lonSpan / 2) * 111 * Math.cos(centerLatitude * Math.PI / 180);
        const actualRadiusKm = Math.min(latRadiusKm, lonRadiusKm);
        
        // Calculate what the expected bounds should be for 300km
        const expectedLatRadiusDegrees = 300 / 111;
        const cosLat = Math.cos(Math.abs(centerLatitude) * Math.PI / 180);
        const expectedLonRadiusDegrees = cosLat > 0.01 ? 300 / (111 * cosLat) : expectedLatRadiusDegrees;
        // Use the smaller radius to match the zoom calculation logic
        const expectedMaxRadiusDegrees = Math.min(expectedLatRadiusDegrees, expectedLonRadiusDegrees);
        const expectedNorth = centerLatitude + expectedMaxRadiusDegrees;
        const expectedSouth = centerLatitude - expectedMaxRadiusDegrees;
        const expectedEast = centerLongitude + expectedMaxRadiusDegrees;
        const expectedWest = centerLongitude - expectedMaxRadiusDegrees;
        

        
        setMapBounds(initialBounds);
      });

      // Handle map movement with improved debouncing
      let moveTimeout: NodeJS.Timeout;
      
      (map as { on: (event: string, handler: () => void) => void }).on("moveend", () => {
        clearTimeout(moveTimeout);
        moveTimeout = setTimeout(() => {
          const bounds = (map as { getBounds: () => { getNorth: () => number; getSouth: () => number; getEast: () => number; getWest: () => number } }).getBounds();
          const newBounds = {
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest(),
          };
          
          // Only update if bounds have changed significantly (more than 0.005 degrees = ~500m)
          const significantChange = !lastBoundsRef.current || 
            Math.abs(lastBoundsRef.current.north - newBounds.north) > 0.005 ||
            Math.abs(lastBoundsRef.current.south - newBounds.south) > 0.005 ||
            Math.abs(lastBoundsRef.current.east - newBounds.east) > 0.005 ||
            Math.abs(lastBoundsRef.current.west - newBounds.west) > 0.005;
          
          if (significantChange) {
            lastBoundsRef.current = newBounds;
            setMapBounds(newBounds);
          }
        }, 500); // Reduced debounce time for faster response
      });
    };

    loadMapbox(initializeMap);

    return () => {
      if (mapRef.current) {
        (mapRef.current as { remove: () => void }).remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerLatitude, centerLongitude, zoomLevel]);

  useEffect(() => {
    //console.log('[PropertyMapSection] places prop length:', places.length);
  }, [places]);

  // Top-level helper
  const getPlaceStyle = (type: string) => {
    switch (type) {
      case "food":
        return { color: "#FF993A", icon: "foodCategory" }; // Orange for food
      case "shopping":
        return { color: "#AB2CFF", icon: "shoppingCategory" }; // Purple for shop
      case "attraction":
        return { color: "#2AD14F", icon: "attractionCategory" }; // Green for attraction
      case "services":
        return { color: "#96CEB4", icon: "servicesCategory" }; // Light green for services
      case "emergency":
        return { color: "#FF6B35", icon: "emergencyCategory" }; // Red for emergency
      default:
        return { color: "#00A09A", icon: "propertyCategory" }; // Teal for default/property
    }
  };

  // Add debug log for selectedPlaceId and hoveredPlaceId
  //console.log('selectedPlaceId', selectedPlaceId, 'hoveredPlaceId', hoveredPlaceId);

  // Top-level memoized features
  const featuresWithStyles = useMemo(() => {
    return (features || []).map(feature => {
      // Normalize type: fallback to 'attraction' if not food/shopping/attraction
      let normalizedType = feature.properties.type;
      if (!["food", "shopping", "attraction", "services", "emergency"].includes(normalizedType)) {
        normalizedType = "attraction";
      }
      const style = getPlaceStyle(normalizedType);
      let iconName = style.icon;
      let selectedReason = '';
      // Only apply _selected logic if not propertyCategory
      if (iconName !== 'propertyCategory') {
        if (feature.properties.id === hoveredPlaceId) {
          iconName = `${style.icon}_selected`;
          selectedReason = 'hovered';
        } else if (feature.properties.id === selectedPlaceId) {
          iconName = `${style.icon}_selected`;
          selectedReason = 'selected';
        }
      }
      //if (iconName.endsWith('_selected')) {
      //  console.log('Marking as selected:', feature.properties.id, selectedReason);
      //}
      return {
        ...feature,
        properties: {
          ...feature.properties,
          color: style.color,
          icon: iconName,
        },
      };
    });
  }, [features, hoveredPlaceId, selectedPlaceId]);

  // Update places on the map when features change, hoveredPlaceId, or selectedPlaceId
  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    
    if (!(map as { isStyleLoaded: () => boolean }).isStyleLoaded()) {
      return;
    }

    const source = (map as { getSource: (id: string) => { setData: (data: unknown) => void } }).getSource("places");
    if (!source) {
      return;
    }

    // Debug log: show only features with _selected icons
    /*
    console.log('selected icons', featuresWithStyles.filter(f => f.properties.icon.endsWith('_selected')).map(f => ({
      id: f.properties.id,
      icon: f.properties.icon
    })));
    */

    // Filter out features with invalid coordinates, color, or icon
    const validFeatures = featuresWithStyles.filter(f => {
      const coords = f.geometry?.coordinates;
      const hasValidCoords =
        Array.isArray(coords) &&
        coords.length === 2 &&
        typeof coords[0] === 'number' &&
        typeof coords[1] === 'number' &&
        !isNaN(coords[0]) &&
        !isNaN(coords[1]);
      const hasColor = typeof f.properties.color === 'string' && f.properties.color;
      const hasIcon = typeof f.properties.icon === 'string' && f.properties.icon;
      if (!hasValidCoords || !hasColor || !hasIcon) {
        return false;
      }
      return true;
    });

    // Always update the source data, even if empty, to ensure smooth updates
    source.setData({
      type: "FeatureCollection",
      features: validFeatures,
    });

    // Force Mapbox to re-render the layer by removing and re-adding it
    if (map.getLayer && map.removeLayer && map.addLayer) {
      try {
        if (map.getLayer('places-circles')) {
          map.removeLayer('places-circles');
        }
        map.addLayer({
          id: 'places-circles',
          type: 'symbol',
          source: 'places',
          layout: {
            'icon-image': ['get', 'icon'],
            'icon-size': 1,
            'icon-allow-overlap': true,
            'icon-ignore-placement': false,
          },
        });
      } catch (e) {
        // Ignore errors if layer is already removed/added
      }
    }

    // Remove and re-add property marker to ensure it's on top
    if ((map as { getLayer: (id: string) => unknown }).getLayer && (map as { removeLayer: (id: string) => void }).removeLayer) {
      try {
        (map as { removeLayer: (id: string) => void }).removeLayer("property-marker");
      } catch (e) { /* ignore if not present */ }
    }
    (map as { addLayer: (layer: unknown) => void }).addLayer({
      id: "property-marker",
      type: "symbol",
      source: "property",
      layout: {
        "icon-image": "property-icon-active",
        "icon-size": 1,
        "icon-allow-overlap": true,
        "icon-offset": [0, -10],
        "symbol-sort-key": 1000,
      },
    });

    // Only load missing icons if initial loading hasn't completed yet
    if (!iconsLoaded) {
      const loadMissingIcons = async () => {
        const loadIcon = async (iconType: string) => {
          if ((map as { hasImage: (id: string) => boolean }).hasImage(iconType)) {
            return; // Icon already loaded
          }
          
          try {
            const response = await fetch(`/assets/img/categoryIcons/${iconType}.svg`);
            if (response.ok) {
              const svgText = await response.text();
              const img = new Image();
              img.onload = () => {
                if ((map as { hasImage: (id: string) => boolean }).hasImage(iconType)) {
                  (map as { removeImage: (id: string) => void }).removeImage(iconType);
                }
                // Use consistent pixelRatio of 5
                (map as { addImage: (id: string, image: HTMLImageElement, options?: { pixelRatio?: number }) => void }).addImage(iconType, img, { pixelRatio: 5 });
              };
              img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
            }
          } catch (error) {
            console.error(`Failed to load missing icon ${iconType}:`, error);
            // Use fallback icon
            try {
              if (!(map as { hasImage: (id: string) => boolean }).hasImage(iconType)) {
                (map as { addImage: (id: string, image: HTMLImageElement) => void }).addImage(iconType, (map as { getImage: (id: string) => HTMLImageElement }).getImage('marker-15'));
              }
            } catch (fallbackError) {
              console.error(`Failed to add fallback icon for ${iconType}:`, fallbackError);
            }
          }
        };
        
        // Load only missing icons using the same constant
        await Promise.allSettled(ICON_VARIANTS.map(loadIcon));
      };
      
      loadMissingIcons();
    }
  }, [featuresWithStyles, iconsLoaded, onPlaceSelect]);

  // Auto-loading is handled by usePlacesMap hook

  // Expose imperative methods
  useImperativeHandle(ref, () => ({
    flyToPlace: (lat: number, lon: number) => {
      if (mapRef.current && typeof mapRef.current.panTo === "function") {
        mapRef.current.panTo([lon, lat]); // Only pan, do not zoom
      } else if (mapRef.current && typeof mapRef.current.flyTo === "function") {
        mapRef.current.flyTo({ center: [lon, lat], zoom: mapRef.current.getZoom && mapRef.current.getZoom() }); // fallback: keep current zoom
      }
    },
    flyToProperty: () => {
      if (mapRef.current && typeof mapRef.current.panTo === "function") {
        mapRef.current.panTo([centerLongitude, centerLatitude]); // Only pan, do not zoom
      } else if (mapRef.current && typeof mapRef.current.flyTo === "function") {
        mapRef.current.flyTo({ center: [centerLongitude, centerLatitude], zoom: mapRef.current.getZoom && mapRef.current.getZoom() }); // fallback: keep current zoom
      }
    },
    getMapBounds: () => {
      if (mapRef.current && typeof mapRef.current.getBounds === "function") {
        const bounds = mapRef.current.getBounds();
        return {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        };
      }
      return undefined;
    },
  }), [centerLatitude, centerLongitude]);

  // Attach event handlers for icon hover/click
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Mouse move (hover)
    const handleMouseMove = (e: MapboxEvent) => {
      if (e.features && e.features[0] && e.features[0].properties && e.features[0].properties.id) {
        onPlaceHover?.(e.features[0].properties.id);
      }
    };
    // Mouse leave (hover out)
    const handleMouseLeave = () => {
      onPlaceHover?.(null);
    };
    // Click (select)
    const handleClick = async (e: MapboxEvent) => {
      if (e.features && e.features[0] && e.features[0].properties && e.features[0].properties.id) {
        const placeId = e.features[0].properties.id;
        // Fetch place data and call onPlaceSelect if provided
        if (onPlaceSelect) {
          const startTime = performance.now();
          const url = `${API_BASE}/places/${placeId}?lat=${centerLatitude}&lon=${centerLongitude}`;
          try {
            const response = await fetch(url);
            if (response.ok) {
              const data = await response.json();
              
              const duration = performance.now() - startTime;
              traceApiResponse(
                url,
                'GET',
                response,
                data,
                duration,
                { propertyId:placeId, lat:centerLatitude, lon:centerLongitude, operation: 'fetch_place_click_data' }
              );

              if (data && data.id) {
                onPlaceSelect(data);
                return;
              }
            }
            // If API fails or returns invalid data, fall back to local
            const localPlace = places?.find((p) => p.id === placeId);
            //console.log('Fallback localPlace:', localPlace, 'for placeId:', placeId, 'in places:', places);
            if (localPlace) {
              onPlaceSelect(localPlace);
            }
          } catch (err) {
            const duration = performance.now() - startTime;
            traceApiError(
              url,
              'GET',
              err as Error,
              { propertyId:placeId, lat:centerLatitude, lon:centerLongitude, operation: 'fetch_place_click_data', duration }
            );
            console.error('[PropertyMapSection] Error fetching place from API:', err);
            // On error, fall back to local
            const localPlace = places?.find((p) => p.id === placeId);
            //console.log('Fallback localPlace:', localPlace, 'for placeId:', placeId, 'in places:', places);
            if (localPlace) {
              onPlaceSelect(localPlace);
            }
          }
        }
      }
    };
    map.on("mousemove", "places-circles", handleMouseMove);
    map.on("mouseleave", "places-circles", handleMouseLeave);
    map.on("click", "places-circles", handleClick);
    // Clean up
    return () => {
      map.off("mousemove", "places-circles", handleMouseMove);
      map.off("mouseleave", "places-circles", handleMouseLeave);
      map.off("click", "places-circles", handleClick);
    };
  }, [mapRef, onPlaceSelect, onPlaceHover, places, centerLatitude, centerLongitude]);

  return (
    <section
      id="property_map_container"
      aria-label="Interactive map showing property and nearby places"
      className="fixed top-0 left-0 md:relative w-full h-map-small flex-grow md:h-full md:w-auto md:flex-1 pointer-events-auto"
    >
      <div className="absolute top-0 left-0 w-full h-full pointer-events-auto">
        <div
          id="property-map"
          className="w-full h-full pointer-events-auto"
          role="application"
          aria-label="Interactive map showing property and nearby places"
        />
      </div>
      

    </section>
  );
});

PropertyMapSection.displayName = "PropertyMapSection"; 